import type {
  ApiError,
  CellDetail,
  IsoDate,
  Outing,
  OutingInput,
  OutingsResponse,
  ScoresResponse,
  Species,
  TimelineResponse,
  ZoneResponse,
} from './contract';

/** Vide : même origine (API simulée). Le vrai serveur se branche avec VITE_API_URL. */
const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ApiFailure extends Error {
  constructor(
    /** 0 quand le réseau ne répond pas. */
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
  get offline() {
    return this.status === 0;
  }
}

let ready: Promise<unknown> = Promise.resolve();

/** Retient les requêtes tant que l'API simulée n'intercepte pas encore fetch. */
export function waitBeforeRequests(promise: Promise<unknown>) {
  ready = promise;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  await ready;
  let res: Response;
  try {
    res = await fetch(`${BASE}/api/v1${path}`, {
      ...init,
      headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
    });
  } catch {
    throw new ApiFailure(0, 'hors_ligne', 'Pas de réseau.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new ApiFailure(res.status, body?.error ?? 'erreur', body?.message ?? `Erreur ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const qs = (params: Record<string, string>) => new URLSearchParams(params).toString();

export const api = {
  zone: () => request<ZoneResponse>('/zone'),
  scores: (species: Species, date: IsoDate, fine?: { resolution: number; bbox: [number, number, number, number] }) =>
    request<ScoresResponse>(
      `/scores?${qs({
        species,
        date,
        ...(fine ? { resolution: String(fine.resolution), bbox: fine.bbox.map((v) => v.toFixed(3)).join(',') } : {}),
      })}`,
    ),
  cell: (h3: string, species: Species, date: IsoDate) =>
    request<CellDetail>(`/cells/${encodeURIComponent(h3)}?${qs({ species, date })}`),
  timeline: (species: Species, bbox: [number, number, number, number]) =>
    request<TimelineResponse>(`/timeline?${qs({ species, bbox: bbox.map((v) => v.toFixed(4)).join(',') })}`),
  postOuting: (outing: OutingInput) => request<Outing>('/outings', { method: 'POST', body: JSON.stringify(outing) }),
  outings: () => request<OutingsResponse>('/outings'),
  /** Extension proposée au contrat v1 : le droit à l'effacement exige de pouvoir supprimer une sortie côté serveur. */
  deleteOuting: (id: string) => request<void>(`/outings/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
